import { defineComponent, computed, reactive, ref, SetupContext } from '@vue/composition-api';
import vueRouter from '@/router/index';
import HttpStatus from 'http-status-codes';
import MessageConst from '@/shared/message-const';
import CommonMessageDialog from '../common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import SystemConst from '@/shared/system-const';
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import { FileOrigin, FilePondFile } from 'filepond'
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import RichTextEditor from '../common/rich-text-editor.component.vue';

// 表示項目の型定義(処理依頼の各ファイル)
type ProcessRequestFiles = {
    fileName: string;                                                           // ファイル名
    fileSequence?: number;                                                      // ファイルシーケンス
    fileSize: number;                                                           // ファイルサイズ
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterCode: string;                                                      // パラメーターコード
    parameterName: string;                                                      // パラメーター名
    value: string;                                                              // パラメーター値
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// 表示項目の型定義（処理依頼）
type ProcessRequest = {
    applicantName: string;                                                      // 氏名
    organizationName: string;                                                   // 所属組織
    postName: string;                                                           // 職
    phoneNumber: string;                                                        // 電話番号
    mail: string;                                                               // メールアドレス
    categoryName: string;                                                       // 依頼内容
    applicationFiles: ProcessRequestFiles[];                                    // 申請ファイル
    attachmentFiles: ProcessRequestFiles[];                                     // 添付ファイル
    remarks: string;                                                            // 備考
    replyComment: string;                                                       // 返信コメント(リッチテキスト)
    replyCommentText: string;                                                   // 返信コメント(文字のみ)
    replyFiles: ProcessRequestFiles[];                                          // 返信ファイル
    approvalStatus: number;                                                     // 承認／否認
}

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                       // ブラウザのサイズ
    visibleApplicant: boolean;                                                  // 申請情報の表示切替
    processRequestId: string;                                                   // 一覧画面からの処理依頼ID
    processRequest: ProcessRequest;                                             // 処理依頼
    validateValues: ValidateType;                                               // m_parametersテーブル
    fileErrorMessage: string;                                                   // 返信ファイルのバリデーションメッセージ文字列
    isFileValid: boolean;                                                       // 返信ファイルのバリデーション結果
    isValidReplyComment: boolean                                                // 返信コメントのバリデーション結果
};

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 *  処理依頼確認コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, FilePond },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                               // 画面遷移用
        const commonRef = ref<any>(null);                                       // メッセージダイアログ
        const form = ref<any>(null);                                            // 入力フォーム
        const pondRef = ref<any>(null);                                         // FilePondへの参照
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            visibleApplicant: true,
            processRequestId: '',
            processRequest: {
                applicantName: '',
                organizationName: '',
                postName: '',
                phoneNumber: '',
                mail: '',
                categoryName: '',
                applicationFiles: [],
                attachmentFiles: [],
                remarks: '',
                replyComment: '',
                replyCommentText: '',
                replyFiles: [],
                approvalStatus: 0
            },
            validateValues: {},
            fileErrorMessage: '',
            isFileValid: true,
            isValidReplyComment: true
        });
        const path = computed(() => ctx.root.$route.params.processRequestId)    // 親画面のパラメータ（処理依頼ID）取得

        // バリデーション用最大桁数
        const maxLength = {
            // 返信コメント
            replyComment: SystemConst.PROCESSING_REQUESTS_MAX_LENGTH.REPLY_COMMENT
        }

        // 返信コメントのバリデーション
        const replyCommentRules = [
            // 桁数
            (v: any) => CommonFunction.checkLength(v, maxLength['replyComment'], '返信コメント')
        ]

        // 返信ファイルのバリデーション
        const filesRules = [
            // 属性（ファイル数、ファイルサイズ、拡張子）
            // 重複（ファイル名）
            () => {
                // API実行によるバリデーション用データがまだ取れていない場合はチェックしない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // 返信ファイルの一覧を得る
                const files: FilePondFile[] = pondRef.value.getFiles();

                // ファイル数が超過していないか
                if (files.length > Number(state.validateValues['fileCount'].value)) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_FILE_COUNT, [
                        '返信',
                        state.validateValues['fileCount'].value + 'ファイル以下']);
                }
                // ファイルサイズが超過または0バイトでないか
                for (const key in files) {
                    if (files[key].fileSize > Number(state.validateValues['fileSize'].value)) {
                        // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_MAX_SIZE, [Number(state.validateValues['fileSize'].value) / 1024]);
                    } else if (files[key].fileSize === 0) {
                        return MessageConst.E_FILE_NO_DATA;
                    }
                }

                // 拡張子が登録対象か
                for (const key in files) {
                    // 登録しようとするファイルの拡張子を得る
                    const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                    const fileExtension = parts[1];

                    // m_parametersの拡張子一覧
                    const validateExtension = state.validateValues['fileExtension'].value.split(',');

                    if (validateExtension.indexOf(fileExtension) === -1) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_EXTENSION, [state.validateValues['fileExtension'].value]);
                    }
                }
                // ファイル名が重複していないか
                if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_FILE_NAME_DUPLICATE;
                }
                return true;
            }
        ]

        // 承認／否認のバリデーション
        const approvalStatusRules = [
            // 承認待ち(0)であれば承認または否認が未選択
            (v: any) =>
                v !== 0 || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['承認 または 否認'])
        ]

        created();
        /**
         * 初期表示
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-clipboard-check-outline');

            // タイトル変更
            ctx.root.$store.commit('setMainTitle', '処理依頼確認');

            // stateへ処理依頼IDを保存する
            state.processRequestId = path.value;

            // 処理依頼確認情報を取得する
            getConfirmation();
        }

        /**
         * ファイルダウンロード
         * @param fileDivision ファイル区分
         * @param fileSequence ファイルシーケンス
         */
        async function download(fileDivision: string, fileSequence: number) {

            // 処理依頼確認ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/processing-requests/{processRequestId}/{fileDivision}/{fileSequence}
            //  processRequestId:処理依頼ID
            //  fileDivision:ファイル区分 ('application' or 'attachment')
            //  fileSequence:ファイルシーケンス
            const params = {
                processRequestId: state.processRequestId,
                fileDivision: fileDivision,
                fileSequence: fileSequence
            }

            // ファイルをダウンロードする
            const response = await ctx.root.$store.dispatch('processingRequests/download', params);

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

        }

        /**
         * 処理依頼確認情報を取得
         */
        async function getConfirmation() {

            // 処理依頼確認取得APIでデータを取得する
            // GET /api/processing-requests/{processRequestId}
            const params = {
                processRequestId: state.processRequestId
            }

            const response = await ctx.root.$store.dispatch('processingRequests/getConfirmation', params);

            // HTTPステータスコードが200以外の場合
            //   404の場合、指定された依頼が存在しない旨のメッセージを表示して親画面へ遷移する
            //   404以外は、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが404の場合、"指定された依頼が存在しない"
                // 旨のメッセージを表示して親画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/processing-requests/list');
                } else {
                    // HTTPステータスコードが400, 404以外はエラー画面へ遷移して処理を終了する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // 処理依頼確認取得APIの結果を
            // state.processingRequest（画面表示用）、及びstate.validateValues（バリデーション用）へ設定する
            state.processRequest = response.data.processingRequest;

            // DBの返信ファイルをfile-pondへ表示する
            response.data.processingRequest.replyFiles.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                pondRef.value.addFile(e.fileName, {
                    type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                    file: {
                        name: e.fileName,       // ファイル名
                        size: e.fileSize        // ファイルサイズ
                    },
                    metadata: {
                        url: `/api/processing-requests/${state.processRequestId}/reply/${e.fileSequence}`   // ダウンロード時のURL
                    }
                });
            });

            // バリデーション用データ
            state.validateValues = response.data.validateValues;
        }

        /**
         * 登録の確認を行い、確認メッセージダイアログを表示する
         */
        function displayDialog() {

            // バリデーションを行う
            //  フォームのバリデーション・返信コメントのバリデーション・返信ファイルのバリデーション
            if (!form.value.validate() || !state.isValidReplyComment || !state.isFileValid) {
                return;
            }

            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じ、処理依頼確認画面へ戻る
            let displayMessage = '';
            displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            commonRef.value.displayConfirmationMessageDialog(displayMessage);

        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {

            // 返信ファイル一覧を得る
            const files: FilePondFile[] = pondRef.value.getFiles();

            // replyFiles（DB上の返信ファイル）が、files（アップロードする返信ファイル）
            // にないものはreplyFilesから消す
            const newReplyFiles: ProcessRequestFiles[] = [];
            state.processRequest.replyFiles.forEach(e => {
                if (files.filter(x => x.file.name === e.fileName).length !== 0) {
                    newReplyFiles.push(e);
                }
            });
            state.processRequest.replyFiles = newReplyFiles;

            // files（アップロードする返信ファイル）をreplyFilesへ追加する。
            // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
            // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
            files.forEach(e => {
                if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                    if (state.processRequest.replyFiles.filter(x => x.fileName === e.file.name).length !== 0) {
                        const r = state.processRequest.replyFiles.find(x => x.fileName === e.file.name);
                        if (r) {
                            // ファイルサイズを更新・ファイルシーケンスを削除
                            r.fileSize = e.fileSize;
                            r.fileSequence = undefined;
                        }
                    } else {
                        // DBに無い、新しいファイル
                        state.processRequest.replyFiles.push({
                            'fileName': e.file.name,
                            'fileSize': e.fileSize
                        });
                    }
                }
            });

            // 処理依頼確認更新APIでデータを更新する
            // POST /api/processing-requests/{processRequestId}

            // APIへ渡すためFilePondFileオブジェクトからFileオブジェクトを作る
            const axiosFiles: File[] = pondRef.value.getFiles().map((e: FilePondFile) => e.file);

            const response = await ctx.root.$store.dispatch(
                'processingRequests/updateRequests',
                {
                    processRequestId: state.processRequestId,
                    processRequest: {
                        'replyComment': state.processRequest.replyComment,
                        'replyCommentText': state.processRequest.replyCommentText,
                        'replyFiles': state.processRequest.replyFiles,
                        'approvalStatus': state.processRequest.approvalStatus
                    },
                    files: axiosFiles
                }
            );

            // HTTPステータスコードが200以外の場合
            // エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが404の場合、"指定された依頼が存在しない"
                // 旨のメッセージを表示して親画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/processing-requests/list');
                } else {
                    // HTTPステータスコードが404以外はエラー画面へ遷移して処理を終了する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            // 処理依頼一覧画面へ遷移する
            commonRef.value.displayMessageTransitionDialog(response, '/processing-requests/list');

        }

        /**
         * FilePondへファイル追加・削除された時のイベント
         */
        function updateFiles() {

            // バリデーション
            for (let index = 0; index < filesRules.length; index++) {
                const rule: any = filesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.fileErrorMessage = valid;
                    state.isFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.fileErrorMessage = '';
                    state.isFileValid = true;
                }
            }

        }

        /**
         * リッチテキストエディタの入力イベント
         * 
         * @param replyComment 返信コメント(HTML形式)
         * @param replyCommentText 返信コメント(文字のみ)
         */
        function textChange(replyComment: string, replyCommentText: string) {
            state.processRequest.replyComment = replyComment;
            state.processRequest.replyCommentText = replyCommentText;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.push('/processing-requests/list');
        }

        // テンプレートへ公開
        return {
            state,
            commonRef,
            form,
            replyCommentRules,
            filesRules,
            approvalStatusRules,
            maxLength,
            pondRef,
            download,
            displayDialog,
            processingData,
            updateFiles,
            textChange,
            back
        };
    }
});
